import { useLayoutEffect, useRef, useState } from "react";

/** Ease-out with a long, calm tail: fast enough to read as "live", never bouncy. */
export function easeOutExpo(t: number): number {
  return t >= 1 ? 1 : 1 - 2 ** (-10 * t);
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false)
  );
}

/**
 * The value to display while a figure rolls towards `target`.
 *
 * - Server and first render return `target` itself, so static markup, tests and
 *   assistive technology always see the real value.
 * - On mount the figure counts up from zero; on a later change it rolls from the
 *   value currently shown, so a refresh or a new blocking period reads as a
 *   change rather than a flash.
 * - `enabled: false` (e.g. the editor canvas), reduced motion, a missing value or a missing animation frame API show the
 *   target at once. Unavailable values are never animated through zero.
 */
export function useAnimatedNumber(
  target: number | null | undefined,
  { duration = 900, enabled = true }: { duration?: number; enabled?: boolean } = {},
): number | null {
  const finite = typeof target === "number" && Number.isFinite(target) ? target : null;
  const [display, setDisplay] = useState<number | null>(finite);
  const shownRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (
      finite === null ||
      !enabled ||
      duration <= 0 ||
      prefersReducedMotion() ||
      typeof requestAnimationFrame !== "function"
    ) {
      shownRef.current = finite;
      setDisplay(finite);
      return;
    }
    const from = shownRef.current ?? 0;
    if (from === finite) {
      setDisplay(finite);
      return;
    }
    let frame = 0;
    let start: number | null = null;
    const step = (now: number) => {
      start ??= now;
      const progress = Math.min(1, (now - start) / duration);
      const value = from + (finite - from) * easeOutExpo(progress);
      shownRef.current = value;
      setDisplay(progress >= 1 ? finite : value);
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    // Paint the starting figure before the first frame, never the target.
    shownRef.current = from;
    setDisplay(from);
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [finite, duration, enabled]);

  return finite === null ? null : display;
}
